import { useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import clsx from 'clsx'
import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import { TourDirectionCard } from 'pages/tours/components/tour-direction-card'
import { useTourDirections } from 'pages/tours/lib/use-tour-directions'

import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types'
import { Button } from 'shared/ui/button'
import { Skeleton } from 'shared/ui/skeleton'

import s from './styles.module.scss'

interface ScrollPosition {
  page: number
  list: number
}

let savedScrollPosition: ScrollPosition | null = null

interface TourDirectionsProps {
  title?: string
  linkText?: string
  button?: {
    actionButton?: () => void
    buttonTitle?: string
  }
  isMainPage?: boolean
}

export const TourDirections = observer(
  ({ title, linkText, button, isMainPage }: TourDirectionsProps) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const listRef = useRef<HTMLUListElement>(null)
    const { directions, isLoading, isError, selectDirection } =
      useTourDirections()

    useEffect(() => {
      if (isLoading || isError || !directions.length || !savedScrollPosition)
        return

      const scrollPosition = savedScrollPosition
      const animationFrame = window.requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosition.page)
        listRef.current?.scrollTo({ left: scrollPosition.list })
        savedScrollPosition = null
      })

      return () => window.cancelAnimationFrame(animationFrame)
    }, [directions.length, isError, isLoading])

    const onViewAll = () => {
      savedScrollPosition = {
        page: window.scrollY,
        list: listRef.current?.scrollLeft || 0,
      }
      navigate(RoutingPaths.tours.allDirections.path())
    }

    if (!isLoading && (isError || !directions.length)) return null

    return (
      <section className={s.container}>
        <div className={s.header}>
          <h2 className={clsx(isMainPage ? 'bold-14' : 'bold-16', s.heading)}>
            {title || t('tours_block.directions.title')}
          </h2>

          <button
            type="button"
            className={s.viewAll}
            onClick={onViewAll}
          >
            <span className={clsx(isMainPage ? 'medium-14' : 'medium-16')}>
              {linkText || t('tours_block.directions.view_all')}
            </span>
            <AppIcon
              id={AllIcons.CHEVRON_RIGHT}
              width={12}
              height={12}
            />
          </button>
        </div>

        {isLoading ? (
          <ul className={s.list}>
            {Array.from({ length: 3 }).map((_, index) => (
              <li
                key={index}
                className={s.card}
              >
                <Skeleton className={s.skeleton} />
              </li>
            ))}
          </ul>
        ) : (
          <ul
            ref={listRef}
            className={s.list}
          >
            {directions.map((direction) => (
              <li
                key={direction.destination_country.id}
                className={s.card}
              >
                <TourDirectionCard
                  direction={direction}
                  onClick={() => selectDirection(direction)}
                />
              </li>
            ))}
          </ul>
        )}

        {button && (
          <div className={s.actionButton}>
            <Button
              variant="grey"
              onClick={button?.actionButton}
            >
              <span className={clsx('semi-bold-16', s.text)}>
                {button?.buttonTitle}
              </span>
            </Button>
          </div>
        )}
      </section>
    )
  },
)
